import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowRight,
  BadgeCheck,
  FileText,
  Rocket,
  ShieldCheck,
  Bike,
  GaugeCircle,
  Landmark,
  Wind,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaTruckPickup, // For 24x7 Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaFileInvoice, // For Return to Invoice
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaUserShield,
  FaCarSide,
} from "react-icons/fa";
import { GiScooter } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const RoyalEnfieldTwoWheelerInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is it mandatory to have insurance for my Royal Enfield?",
      answer:
        "Yes, third-party insurance is legally required for all motor vehicles in India.",
    },
    {
      question:
        "Which is better — third-party or comprehensive insurance for Royal Enfield?",
      answer:
        "Comprehensive insurance is better as it protects your own bike in addition to covering third-party liabilities.",
    },
    {
      question: "How much is the insurance for a Royal Enfield Classic 350?",
      answer:
        "Starting from ₹1,500 depending on variant, location, and add-ons.",
    },
    {
      question: "Does Royal Enfield insurance cover off-road accidents?",
      answer:
        "Only if off-roading is not in violation of traffic laws and you have a valid comprehensive policy.",
    },
    {
      question: "Can I transfer my insurance if I sell my Royal Enfield?",
      answer:
        "Yes, insurance can be transferred along with ownership. The NCB remains with the original owner.",
    },
    {
      question: "How does IDV affect my premium?",
      answer:
        "A higher IDV (bike's market value) leads to higher premiums but also better claim payouts.",
    },
    {
      question:
        "What is the benefit of Zero Depreciation for Royal Enfield bikes?",
      answer:
        "It ensures full claim value without depreciation deductions on parts.",
    },
    {
      question: "Is engine damage due to water covered?",
      answer: "Only if you opt for an engine protection add-on.",
    },
    {
      question: "Are modifications on Royal Enfield covered?",
      answer:
        "Cosmetic modifications may be covered if declared. Performance mods are often excluded.",
    },
    {
      question:
        "Does insurance cover accessories like saddlebags or windshield?",
      answer: "Only if declared and covered as part of accessories insurance.",
    },
    {
      question:
        "Is insurance available for older RE bikes like Electra or Thunderbird?",
      answer: "Yes, but coverage may vary depending on age and condition.",
    },
    {
      question: "Can I get insurance for Royal Enfield Interceptor 650?",
      answer:
        "Yes, most major insurers offer tailored plans for high-end RE models.",
    },
    {
      question: "How to claim insurance after an accident?",
      answer:
        "Inform your insurer immediately, file an FIR if needed, take photos, and visit a network garage.",
    },
    {
      question: "Is roadside assistance useful for RE riders?",
      answer:
        "Absolutely — especially for long-distance travellers and solo riders.",
    },
    {
      question: "What is NCB and how does it help RE owners?",
      answer:
        "No Claim Bonus gives a discount on renewal premium if no claims are made in the previous year.",
    },
    {
      question: "Can I renew my policy online?",
      answer:
        "Yes, you can easily renew it online via TheFinMaster without physical paperwork.",
    },
    {
      question: "Is off-roading covered under RE insurance?",
      answer:
        "Not typically, unless it's within legal bounds and you have comprehensive coverage.",
    },
    {
      question: "Can I choose my own garage for repairs?",
      answer:
        "Yes, but for cashless service, it's best to visit an insurer-network garage.",
    },
    {
      question: "What happens if I don’t renew my insurance on time?",
      answer:
        "Policy lapses, leading to fines, loss of NCB, and potential vehicle inspection.",
    },
    {
      question: "Does TheFinMaster help with comparing Royal Enfield policies?",
      answer:
        "Yes, TheFinMaster offers tools to compare insurers, premiums, add-ons, and claim processes in one place.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description:
        "Essential for Classic 350, Meteor, and Interceptor owners to avoid out-of-pocket expenses.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description:
        "Especially for Himalayan or bikes used in flood-prone or off-road areas.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "A must-have for highway travellers or solo riders.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Covers non-reimbursable parts like engine oil, nuts, and washers.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description:
        "Useful for new bike owners to get full value in case of theft or total loss.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];

  const benefits = [
    {
      title: "Classic 350",
      desc: "Iconic design with modern upgrades.",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Hunter 350",
      desc: "Lightweight urban-friendly roadster.",
      icon: <ShieldCheck className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "Meteor 350",
      desc: "Comfortable touring cruiser.",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Bullet 350",
      desc: "A timeless classic in a new avatar.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "Himalayan 450",
      desc: "Adventure-ready touring machine.",
      icon: <Landmark className="w-7 h-7 text-pink-500" />,
      gradient: "from-pink-50 to-pink-100",
    },
    {
      title: "Interceptor 650 / GT 650",
      desc: "Twin-cylinder performance cruisers.",
      icon: <Wind className="w-7 h-7 text-teal-500" />,
      gradient: "from-teal-50 to-teal-100",
    },
  ];
  const royalinsurance = [
    {
      name: "Hunter350",
      path: "/royal-enfield-hunter350-bike-insurance",
      image: "/assets/roaylenfieldhunter350.jpeg",
    },
    {
      name: "Classic350",
      path: "/royal-enfield-classic350-bike-insurance",
      image: "/assets/roaylenfieldclassic350.jpeg",
    },
    {
      name: "Meteor350",
      path: "/royal-enfield-meteor350-bike-insurance",
      image: "/assets/roaylenfieldmeteor350.jpeg",
    },
    {
      name: "Bullet350",
      path: "/royal-enfield-bullet350-bike-insurance",
      image: "/assets/roaylenfieldbullet350.jpeg",
    },
    {
      name: "Scram411",
      path: "/royal-enfield-scram411-bike-insurance",
      image: "/assets/roaylenfieldscram411.jpeg",
    },
    {
      name: "Himalayan411",
      path: "/royal-enfield-himalayan411-bike-insurance",
      image: "/assets/roaylenfieldhimalayan411.jpeg",
    },
    {
      name: "Interceptor 650",
      path: "/royal-enfield-interceptor650-bike-insurance",
      image: "/assets/roaylenfieldinterceptor650.jpeg",
    },
      {
      name: "ContinentalGT 650",
      path: "/royal-enfield-continental-gt650-bike-insurance",
      image: "/assets/roaylenfieldcontinentalGT650.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Royal Enfield Two-Wheeler Insurance in India – Compare Quotes & Buy
          Online
        </title>
        <meta
          name="description"
          content="Explore Royal Enfield two-wheeler insurance options in India. Compare quotes, customize coverage, and buy online to protect your Royal Enfield bike."
        />
        <meta
          name="keywords"
          content="royal enfield two-wheeler insurance, bike insurance india, royal enfield classic 350 insurance, royal enfield hunter 350 insurance, royal enfield himalayan insurance, two-wheeler insurance quotes"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/royal-enfield-two-wheeler-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/royal-enfield-two-wheeler-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Royal Enfield Two-Wheeler Insurance in India" />
        <meta property="og:description" content="Compare and buy Royal Enfield two-wheeler insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/royal-enfield-two-wheeler-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">
              Royal Enfield Two-Wheeler Insurance
            </span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Royal Enfield Two-Wheeler Insurance{" "}
                  </span>{" "}
              
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  Royal Enfield is an icon on Indian roads, known for its
                  classic design, robust build, and powerful performance.
                  Whether you ride a timeless Classic 350 or a long-haul
                  Himalayan, protecting your bike starts with the right
                  insurance plan. This guide walks you through everything you
                  need to know about insuring a Royal Enfield, from average
                  premiums and model insights to useful add-ons, renewal tips,
                  and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/royalinsurance.jpeg"
                alt="Royal Enfield Two-Wheeler"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About Royal Enfield in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Founded in 1901, Royal Enfield has a cult following in India and
            across the globe. It blends vintage design with modern engineering,
            making it one of the most recognizable motorcycle brands in the
            country.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="p-4 sm:p-8 bg-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
          Choose Your Bike Brand
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {royalinsurance.map((calc, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.04 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={calc.path}
                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
              >
                <img
                  src={calc.image}
                  alt={calc.name}
                  className="w-16 h-16 object-contain mb-2"
                />
                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                  {calc.name}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Insurance is Essential for Royal Enfield Riders
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Owning a Royal Enfield is often about long rides, weekend escapes,
            and a deeper connection with the road. Insurance helps you enjoy the
            journey without worry. Here’s why coverage is crucial:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>
                Protects your investment from theft, accidents, and disasters
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>
                Covers expensive parts and repairs specific to RE models
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Legally mandatory under Indian Motor Vehicle laws</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Peace of mind during road trips and highway touring</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>
                Enables cashless claims at authorized RE service centers
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your Royal Enfield stays protected,
            legally compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your TwoWheeler Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/bike-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Royal Enfield Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Below are the estimated insured declared values (IDV) and starting
          insurance premiums for key Royal Enfield models:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Royal Enfield Hunter 350",
                category: "Urban Roadster",
                idv: "1.4 – 1.6 Lakh",
                premium: "₹1,400 – ₹1,700",
              },
              {
                model: "Royal Enfield Classic 350",
                category: "Retro Cruiser",
                idv: "1.6 – 1.8 Lakh",
                premium: "₹1,500 – ₹2,000",
              },
              {
                model: "Royal Enfield Meteor 350",
                category: "Touring Cruiser",
                idv: "1.8 – 2.0 Lakh",
                premium: "₹1,600 – ₹2,100",
              },
              {
                model: "Royal Enfield Bullet 350",
                category: "Heritage Bike",
                idv: "1.6 – 1.75 Lakh",
                premium: "₹1,500 – ₹1,900",
              },
              {
                model: "Royal Enfield Himalayan 450",
                category: "Adventure Tourer",
                idv: "2.6 – 2.9 Lakh",
                premium: "₹2,300 – ₹3,000",
              },
              {
                model: "Interceptor 650 / GT 650",
                category: "Twin-Cylinder Cruiser",
                idv: "3.3 – 3.6 Lakh",
                premium: "₹2,800 – ₹3,400",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Royal Enfield Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Royal Enfield Hunter 350",
                    "Urban Roadster",
                    "1.4 – 1.6 Lakh",
                    "₹1,400 – ₹1,700",
                  ],
                  [
                    "Royal Enfield Classic 350",
                    "Retro Cruiser",
                    "1.6 – 1.8 Lakh",
                    "₹1,500 – ₹2,000",
                  ],
                  [
                    "Royal Enfield Meteor 350",
                    "Touring Cruiser",
                    "1.8 – 2.0 Lakh",
                    "₹1,600 – ₹2,100",
                  ],
                  [
                    "Royal Enfield Bullet 350",
                    "Heritage Bike",
                    "1.6 – 1.75 Lakh",
                    "₹1,500 – ₹1,900",
                  ],
                  [
                    "Royal Enfield Himalayan 450",
                    "Adventure Tourer",
                    "2.6 – 2.9 Lakh",
                    "₹2,300 – ₹3,000",
                  ],
                  [
                    "Interceptor 650 / GT 650",
                    "Twin-Cylinder Cruiser",
                    "3.3 – 3.6 Lakh",
                    "₹2,800 – ₹3,400",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons to Consider with Royal Enfield Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          RE bikes are built for long-distance riding and tough terrain. Here
          are some valuable add-ons to enhance your coverage:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurance Providers for Royal Enfield
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here are some insurers with strong track records and tailor-made
          options for RE riders:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for quick settlements and extensive garage tie-ups.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Digit Insurance
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Transparent digital-first process.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Strong presence and 24x7 claims assistance.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Great for high-value bikes like Interceptor.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              TATA AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Flexible add-ons for tourers and adventurers.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Get the Best Royal Enfield Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To get the best premium rates and coverage benefits, follow these
              smart tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare premiums from multiple providers using a tool like
                  TheFinMaster.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Retain your No Claim Bonus (NCB) to reduce renewal premiums.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Avoid small claims to protect your NCB benefit.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install AIS140-compliant GPS or anti-theft devices to get
                  discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for long-term policies (3-year third-party) to save on
                  renewals.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Needed to Insure a Royal Enfield
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Keep these handy for a smooth insurance process:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>RC Book (Vehicle Registration)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing policy (for renewal)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid ID proof</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-sized photo (if required)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>PUC certificate</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About Royal Enfield Bike Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <GiScooter />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Royal Enfield bikes are built for character, comfort, and
                journeys worth remembering. But even legends need protection. A
                well-chosen insurance policy ensures you ride worry-free —
                whether you're commuting in the city or exploring mountain
                trails.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                With TheFinMaster, getting the right insurance for your Royal
                Enfield is simple. Explore plans, compare add-ons, and protect
                your ride the smart way.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default RoyalEnfieldTwoWheelerInsurance;
